<script setup lang="ts">
const env = useRuntimeConfig();
const { data: config } = useProjectInfo();
const funders = computed(() => config.value?.projectConfig?.funders ?? []);
const partners = computed(() => config.value?.projectConfig?.partners ?? []);
const isExpanded = ref(false);

function expandFooter() {
	isExpanded.value = true;
}
</script>

<template>
	<!-- eslint-disable-next-line vuejs-accessibility/no-static-element-interactions -->
	<footer
		class="relative z-50 h-7 bg-surface text-on-surface"
		@focusout="isExpanded = false"
		@mouseleave="isExpanded = false"
	>
		<div class="flex items-center gap-2 border px-8 py-1.5 text-xs">
			<button
				aria-label="Expand footer"
				class="h-4 flex-1"
				type="button"
				@focus="expandFooter"
				@mouseover="expandFooter"
			></button>
			<span class="flex gap-1">
				<span>&copy; {{ new Date().getUTCFullYear() }}</span>
				<a class="hover:underline hover:underline-offset-2" href="https://www.oeaw.ac.at/acdh">
					ACDH
				</a>
			</span>
			<span>|</span>
			<NuxtLink class="hover:underline hover:underline-offset-2" href="/imprint">Imprint</NuxtLink>
			<span>|</span>
			<span class="flex gap-1">
				<span>Frontend: {{ env.public.currentGitSha }}</span>
			</span>
			<span>|</span>
			<span class="flex gap-1">
				<span>Backend: {{ config?.projectConfig?.version?.backend }}</span>
			</span>
			<span>|</span>
			<span class="flex gap-1">
				<span>Data: {{ config?.projectConfig?.version?.data }}</span>
			</span>
			<button
				aria-label="Expand footer"
				class="h-4 flex-1"
				type="button"
				@focus="expandFooter"
				@mouseover="expandFooter"
			></button>
		</div>
		<div
			id="footer-content"
			class="absolute inset-x-0 bottom-full w-full bg-neutral-50 px-0 text-gray-900 transition-transform duration-500"
			:class="isExpanded ? 'pointer-events-auto' : 'pointer-events-none'"
			:style="{
				transform: isExpanded ? 'translateY(0)' : 'translateY(calc(100% + 2rem))',
			}"
		>
			<div id="footer-full-content" class="mx-auto w-full px-4" tabindex="-1">
				<div
					class="footer-logo-widget mb-4 flex items-center border-b pt-2 pb-4 text-sm font-semibold"
				>
					<svg
						class="mr-1 w-4 max-w-full align-bottom"
						fill="none"
						height="24"
						stroke="currentColor"
						stroke-linecap="round"
						stroke-linejoin="round"
						stroke-width="2"
						viewBox="0 0 24 24"
						width="24"
						xmlns="http://www.w3.org/2000/svg"
					>
						<path
							d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"
						></path>
					</svg>
					CONTACT
				</div>
				<div class="-mx-4 flex flex-wrap">
					<div class="mt-4 w-1/4 flex-none px-4 sm:w-1/6 md:w-1/6 lg:w-1/12">
						<div class="">
							<a href="https://www.oeaw.ac.at/acdh" rel="noopener" target="_blank">
								<img
									alt="ACDH Logo"
									class="max-w-full h-auto"
									src="https://fundament.acdh.oeaw.ac.at/common-assets/images/acdh_logo.svg"
									title="ACDH Logo"
								/>
							</a>
						</div>
					</div>
					<!-- .footer-widget -->
					<div
						class="mt-4 max-w-[33.333%] flex-none px-4"
						:class="
							((funders.length ?? 0) || (partners.length ?? 0)) > 0
								? 'w-auto md:w-auto lg:w-auto'
								: 'w-3/4 sm:w-1/2 md:w-1/3 lg:w-1/3'
						"
					>
						<div>
							<p class="mt-0 mb-4 block">
								ACDH
								<br />
								Austrian Centre for Digital Humanities
								<br />
								Austrian Academy of Sciences
							</p>
							<p class="mt-0 mb-4 block">
								Bäckerstraße 13
								<br />
								1010 Vienna
							</p>
							<p class="mt-0 mb-4 block">
								T: +43 1 51581-2200
								<br />
								E:
								<a href="mailto:acdh@oeaw.ac.at">acdh@oeaw.ac.at</a>
							</p>
						</div>
					</div>
					<!-- .footer-widget -->
					<div
						class="mt-4 ml-auto w-full max-w-[33.333%] flex-none shrink-0 grow-0 px-4 sm:w-1/3 md:w-1/3 lg:w-1/4"
						:class="(funders.length ?? 0) > 0 ? 'basis-1/4' : 'basis-1/3'"
					>
						<h6 class="mb-2 block font-bold">HELPDESK</h6>
						<p class="mt-0 mb-4 block">
							ACDH runs a helpdesk offering advice for questions related to various digital
							humanities topics.
						</p>
						<p class="mt-0 mb-4 block">
							<a
								class="block max-w-xs rounded-3xl border border-[#88dbdf] bg-white px-5 py-1 text-xs font-semibold text-[#88dbdf] transition-colors duration-200 ease-in-out hover:bg-[#88dbdf] hover:text-white"
								href="mailto:acdh-helpdesk@oeaw.ac.at"
							>
								ASK US!
							</a>
						</p>
					</div>
					<!-- .footer-widget -->
					<div
						v-if="(partners.length ?? 0) > 0"
						class="mt-4 ml-auto w-full max-w-[33.333%] shrink-0 grow-0 basis-1/4 px-4 sm:w-1/3 md:w-1/3 lg:w-1/4"
					>
						<h6 class="sr-only mb-2 block font-bold">Funding</h6>
						<template v-for="partner in partners" :key="partner.partnerLink">
							<img
								:alt="partner.logoID"
								class="mt-2 mb-4 block"
								:src="
									partner.logoID?.startsWith('http')
										? partner.logoID
										: `/assets/images/${partner.logoID}.png`
								"
							/>
							<p class="mt-0 mb-4 block text-sm">
								{{ partner.text }}
								<a
									class="text-[#88dbdf] transition duration-200 ease-in-out hover:brightness-75"
									:href="partner.partnerLink ?? '#'"
									target="_blank"
								>
									{{ partner.partnerLink }}
								</a>
							</p>
						</template>
					</div>
					<!-- .footer-widget -->
					<div
						v-if="(funders.length ?? 0) > 0"
						class="mt-4 ml-auto w-full max-w-[33.333%] shrink-0 grow-0 basis-1/4 px-4 sm:w-1/3 md:w-1/3 lg:w-1/4"
					>
						<h6 class="sr-only mb-2 block font-bold">Funding</h6>
						<template v-for="funder in funders" :key="funder.funderLink">
							<img
								:alt="funder.logoID"
								class="mt-2 mb-4 block"
								:src="
									funder.logoID?.startsWith('http')
										? funder.logoID
										: `/assets/images/${funder.logoID}.png`
								"
							/>
							<p class="mt-0 mb-4 block text-sm">
								{{ funder.text }}
								<a
									class="text-[#88dbdf] transition duration-200 ease-in-out hover:brightness-75"
									:href="funder.funderLink"
									target="_blank"
								>
									{{ funder.funderLink }}
								</a>
							</p>
						</template>
					</div>
					<!-- .footer-widget -->
				</div>
			</div>
		</div>
	</footer>
</template>

<style>
@media (width >= 640px) {
	/* sm */
	#footer-full-content {
		max-width: 540px;
	}
}

@media (width >= 768px) {
	/* md */
	#footer-full-content {
		max-width: 720px;
	}
}

@media (width >= 1024px) {
	/* lg */
	#footer-full-content {
		max-width: 960px;
	}
}

@media (width >= 1200px) {
	/* xl */
	#footer-full-content {
		max-width: 1140px;
	}
}

.footer-logo-widget {
	flex: 0 0 8.333%;
	max-width: 8.333%;
}
</style>
